<template>
    <div class="charges-editor-page">
        <div class="md-layout">
            <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100">
                <md-card class="custom-card">
                    <md-card-content class="body-list">
                        <div
                            :class="[{ fixed: isFixedSelector }]"
                            class="bulk-section sticky-selection"
                            v-if="selectedJobs.length > 1"
                        >
                            <div>{{ selectedJobs.length }} job{{ selectedJobs.length > 1 ? 's' : '' }} selected.</div>
                            <div>
                                <md-button
                                    class="md-round md-just-icon md-success"
                                    title="Recalculate jobs"
                                    @click="bulkRecalculate"
                                >
                                    <md-icon>currency_exchange</md-icon>
                                </md-button>
                                <md-button
                                    class="md-round md-just-icon md-success"
                                    title="Approve jobs"
                                    @click="bulkApprove"
                                >
                                    <md-icon>thumb_up</md-icon>
                                </md-button>
                                <md-button
                                    class="md-round md-just-icon md-danger"
                                    title="Unapprove jobs"
                                    @click="bulkUnapprove"
                                >
                                    <md-icon>thumb_down</md-icon>
                                </md-button>
                            </div>
                        </div>
                        <ProcessChargesTable :is-orders="true">
                            <template v-slot:table-header>
                                <md-checkbox v-model="isSelectAll" @change="onSelectAll(isSelectAll)"></md-checkbox>
                                <table-header-dropdown
                                    class="reference-column"
                                    column-icon="keyboard_arrow_down"
                                    :default-text="tableColumnOptions.columns[columnKeys.referenceSource].name"
                                    :dropdown-options="tableColumnKeyOptions.referenceSource"
                                    :selected-option="tableColumnOptions.columns[columnKeys.referenceSource]"
                                    @selectedOption="
                                        $_handleColumnSwitch(
                                            $event,
                                            columnKeys.referenceSource,
                                            'stops.defaults.stop-table-options'
                                        )
                                    "
                                />
                                <h5>Order Date</h5>
                                <h5>Customer</h5>
                                <h5>Service Pkg</h5>
                                <h5 class="time-header">Time</h5>
                                <h5 class="hide-column">Attempt #</h5>
                                <h5 class="hide-column">Run #</h5>
                                <h5>Status</h5>
                                <h5>Delivered By</h5>
                                <table-header-dropdown
                                    class="finance-column"
                                    default-text="Total Charge"
                                    :dropdown-options="totalChargeDropdownList"
                                    :selected-option="selectedTotalChargeDropdowns"
                                    @selectedOption="handleSelectTotalChargeFilter"
                                />
                                <table-header-dropdown
                                    class="finance-column"
                                    default-text="Total Cost"
                                    :dropdown-options="totalCostDropdownList"
                                    :selected-option="selectedTotalCostDropdowns"
                                    @selectedOption="handleSelectTotalCostFilter"
                                />
                                <div class="finance-column hide-column">
                                    <h5>Margin</h5>
                                </div>
                                <h5>Actions</h5>
                            </template>
                            <template
                                v-slot:table-content
                                v-if="
                                    jobChargeList !== null &&
                                        jobChargeList !== undefined &&
                                        jobChargeList.length > 0 &&
                                        !isLoading
                                "
                            >
                                <div v-for="(item, index) in jobChargeList" :key="index">
                                    <ActiveRow
                                        :root-job="item"
                                        :selected-jobs="selectedJobs"
                                        :has-expanded-row="item.hasExpandedRow"
                                        :reference-header-value="tableColumnOptions.columns[columnKeys.referenceSource]"
                                        :is-cost-approval-feature-enabled="isCostApprovalFeatureEnabled"
                                        :is-charge-approval-feature-enabled="isChargeApprovalFeatureEnabled"
                                        :service-packages="servicePackages"
                                        @row-click="handleRowClick(index)"
                                        @update-rates="updateRates(item)"
                                        @approve-jobs="approveJobs(item)"
                                        @unapprove-jobs="unapproveJobs(item)"
                                        @update-service-package="updateServicePackage(item)"
                                        @update-time="handleTimeUpdated"
                                    >
                                        <template v-slot:nested-table v-if="item.childrenJobs.length > 0">
                                            <NestedTable
                                                :root-job="item"
                                                :selected-jobs="selectedJobs"
                                                :reference-header-value="
                                                    tableColumnOptions.columns[columnKeys.referenceSource]
                                                "
                                                @update-rates="updateRates"
                                                @select-items="handleNestedItemsSelected"
                                                @deselect-items="handleNestedItemsDeselected"
                                            />
                                        </template>
                                    </ActiveRow>
                                </div>
                            </template>
                            <template v-else v-slot:table-content>
                                <div v-if="isLoading" class="content-loader loading-position">
                                    <fade-loader :loading="isLoading" color="#333333" />
                                    <span>LOADING</span>
                                </div>
                                <p v-else class="no-result-message">
                                    No results matching your search/filter could be found.
                                </p>
                            </template>
                        </ProcessChargesTable>
                    </md-card-content>
                </md-card>
            </div>
        </div>
        <md-card-actions class="page-footer" md-alignment="space-between">
            <div>
                <p v-if="total === pagination.perPage" class="card-category">
                    Page {{ pagination.currentPage }} of many
                </p>
                <p v-else class="card-category">Page {{ pagination.currentPage }} of {{ totalPages }}</p>

                <p>Total Jobs: {{ pagination.total }}</p>
            </div>
            <pagination
                v-model="pagination.currentPage"
                class="pagination-no-border pagination-success"
                :per-page="pagination.perPage"
                :total="total"
                @change-page="handleChangePage($event, pagination.perPage)"
            />
        </md-card-actions>

        <div class="progress-container" v-if="showProgressBox">
            <div class="button-close">
                <md-button class="md-default md-just-icon md-round" @click="handleCloseProgressBox">
                    <md-icon>close</md-icon>
                </md-button>
            </div>
            <div class="progress-text-section" v-if="!isProgressComplete">
                <div>
                    <div class="btn-loader route-loader"></div>
                </div>
                <div v-if="totalToRecalculate == 0">Preparing...</div>
                <div v-else>{{ currentProcessed }} of {{ totalToRecalculate }} have been recalculated.</div>
            </div>
            <div v-else class="progress-text-section">
                <div>
                    <md-icon class="green-check">check_circle</md-icon>
                </div>
                <div>Recalculated Jobs.</div>
            </div>
        </div>
    </div>
</template>

<script>
import { PAGINATION_DEFAULTS, STOP_TABLE_HEADER_DISPLAY_OPTION } from '@/utils/defaults';
import { Pagination, TableHeaderDropdown } from '@/components';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { FeatureManager } from '@/directives';
import { handleRequests, showErrorMessage } from '@/helpers';
import { mapGetters } from 'vuex';
import {
    PROCESS_TOTALS_CONSTANTS,
    REFERENCE_SOURCE_KEY_OPTIONS,
    FEATURE_NAMES
} from '@/utils/constants';
import FadeLoader from 'vue-spinner/src/FadeLoader';
import { NestedTable, ProcessChargesTable, ActiveRow } from './components';

export default {
    name: 'ProcessChargesOrders',
    components: {
        NestedTable,
        ActiveRow,
        ProcessChargesTable,
        Pagination,
        FadeLoader,
        TableHeaderDropdown
    },
    mixins: [GeneralMixin, FeatureManager],
    props: {
        filter: {
            type: Object,
            default: null
        },
        servicePackages: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            isLoading: false,
            isSelectAll: false,
            jobChargeList: [],
            selectedJobs: [],
            pagination: PAGINATION_DEFAULTS,
            fromDateFilterValue: '',
            toDateFilterValue: '',
            expandedRow: null,
            newRow: null,
            loadCapacityList: [],
            selectedRow: null,
            isRecalculating: false,
            shouldCloseDropdown: false,
            filters: null,
            totalCostDropdownList: PROCESS_TOTALS_CONSTANTS,
            selectedTotalCostDropdowns: PROCESS_TOTALS_CONSTANTS[0],
            totalChargeDropdownList: PROCESS_TOTALS_CONSTANTS,
            selectedTotalChargeDropdowns: PROCESS_TOTALS_CONSTANTS[0],
            showProgressBox: false,
            totalToRecalculate: 0,
            currentProcessed: 0,
            isProgressComplete: false,
            isFixedSelector: false,
            isCostApprovalFeatureEnabled: false,
            isChargeApprovalFeatureEnabled: false,
            headerDropdown: REFERENCE_SOURCE_KEY_OPTIONS,
            ...STOP_TABLE_HEADER_DISPLAY_OPTION()
        };
    },
    computed: {
        currentPageMax() {
            const highBound = this.currentPageMin + this.pagination.perPage;
            return this.total < highBound ? this.total : highBound;
        },
        currentPageMin() {
            return this.pagination.perPage * (this.pagination.currentPage - 1);
        },
        total() {
            return this.pagination.total;
        },
        totalPages() {
            if (this.total > 0) {
                return Math.ceil(this.total / this.pagination.perPage);
            }
            return 1;
        },
        ...mapGetters({
            user: 'user/user',
            isReadOnlyUser: 'user/isReadOnlyUser'
        })
    },
    async created() {
        this.$_applyColumnSwitchDefaults('stops.defaults.stop-table-options', {
            [this.columnKeys.referenceSource]: REFERENCE_SOURCE_KEY_OPTIONS
        });
    },
    watch: {
        filter(newVal, oldVal) {
            this.filters = Object.assign(this.filters, this.filter);
            this.handleFilterChanged(this.filters);
        }
    },
    async updated() {
        this.toggleTableColumns();
    },
    async mounted() {
        this.filters = this.filter;

        this.loadCapacityList = this.user.vehicleCapacityUnitsConfiguration || [];

        if (localStorage.getItem('processCharge.search-filter-options') !== null) {
            const localStorageOptions = JSON.parse(localStorage.getItem('processCharge.search-filter-options'));
            if (localStorageOptions) {
                this.filters = localStorageOptions;
            }
        }

        await this.loadJobs();
        document.querySelector('.main-panel').addEventListener('scroll', this.handleScroll, true);

        this.isCostApprovalFeatureEnabled = false; // hardcoded to false for now since costs are not implemented for orders.
        this.isChargeApprovalFeatureEnabled = this.$_hasFeature(FEATURE_NAMES.JobsChargeApproval);
    },
    beforeDestroy() {
        document.querySelector('.main-panel').removeEventListener('scroll', this.handleScroll);
    },
    methods: {
        /**
         * Check if the job matches the identifiers.
         * Running time: O(n) where n is the number of identifiers
         * @param job
         * @param identifiers
         * @returns {*|boolean}
         */
        checkIfJobMatches(job, identifiers) {
            const orderIds = identifiers.filter((i) => i.identifierType === 'orderId').map((i) => i.identifier);
            const stopIds = identifiers.filter((i) => i.identifierType === 'stopId').map((i) => i.identifier);

            if (job.orderId) {
                return orderIds.includes(job.orderId);
            }
            if (job.stopId) {
                return stopIds.includes(job.stopId);
            }
            return false;
        },
        /**
         *
         * @param {{ identifierType: "stopId" | "orderId", identifier: number }[]} items
         */
        handleNestedItemsDeselected(items) {
            const jobsFlattened = [];
            // eslint-disable-next-line no-restricted-syntax
            for (const job of this.jobChargeList) {
                jobsFlattened.push(job);
                jobsFlattened.push(...job.childrenJobs);
            }
            const jobsToDeselect = jobsFlattened.filter((job) => this.checkIfJobMatches(job, items));
            this.selectedJobs = this.selectedJobs.filter((job) => !jobsToDeselect.includes(job));
        },
        /**
         *
         * @param {{ identifierType: "stopId" | "orderId", identifier: number }[]} items
         */
        handleNestedItemsSelected(items) {
            // since there are childrenJobs, we need to flatten the list
            const jobsFlattened = [];

            // eslint-disable-next-line no-restricted-syntax
            for (const job of this.jobChargeList) {
                jobsFlattened.push(job);
                jobsFlattened.push(...job.childrenJobs);
            }
            // if an item in the list is already selected, we should remove it
            const newlySelectedJobs = jobsFlattened.filter((job) => this.checkIfJobMatches(job, items));
            // 1. get the intersection of selectedJobs and this.selectedJobs
            // 2. remove the intersection from this.selectedJobs and selectedJobs
            // 3. add the remaining selectedJobs to this.selectedJobs
            const intersection = this.selectedJobs.filter((job) => newlySelectedJobs.includes(job));
            const remainingSelectedJobs = newlySelectedJobs.filter((job) => !intersection.includes(job));
            this.selectedJobs = [...this.selectedJobs, ...remainingSelectedJobs];
        },
        handleTimeUpdated(payload) {
            const {
                orderId,
                newTimes: { actualStartTime, actualEndTime },
                stopId
            } = payload;

            const order = this.jobChargeList.find((t) => t.orderId === orderId);
            const stop = this.jobChargeList.find((s) => s.stopId === stopId);

            if (order) {
                order.actualStartTime = actualStartTime;
                order.actualEndTime = actualEndTime;
            }

            if (stop) {
                stop.actualStartTime = actualStartTime;
                stop.actualEndTime = actualEndTime;
            }
        },
        async fetchAllCompletedJobs(pageNumber = 1, itemsPerPage = 50) {
            this.isLoading = true;
            const endpoint = `/api/process-charges/list/orders`;
            const response = await handleRequests(endpoint, {
                params: {
                    pageNumber,
                    itemsPerPage,
                    ...this.filters
                }
            });

            this.isLoading = false;
            return response;
        },
        async loadJobs(pageNumber = 1, perPage = 50) {
            await this.handleFetchAllJobs(pageNumber, perPage);
        },
        async handleFetchAllJobs(pageNumber = 1, perPage = 50) {
            if (this.isLoading) {
                return;
            }
            const {
                data: { jobs, totalJobs }
            } = await this.fetchAllCompletedJobs(pageNumber, perPage);

            this.selectedRow = null;
            this.expandedRow = null;

            this.assignJobcharges(jobs, totalJobs);
            // reset selected jobs
            this.selectedJobs = [];
        },
        assignJobcharges(jobs, totalJobs) {
            this.jobChargeList = jobs;
            this.pagination.total = totalJobs;
        },
        onSelectAll(value) {
            if (this.expandedRow) {
                this.removeOpenedRow();
            }

            this.selectedJobs = [];

            if (value) {
                this.jobChargeList.forEach((job) => {
                    this.selectedJobs.push(job);
                });
            } else {
                this.selectedJobs = [];
                this.shouldCloseDropdown = true;
            }
        },
        async handleChangePage(currentPage = 1, perPage = 50) {
            if (this.isLoading) {
                return;
            }

            this.$_handleLoaderState(true);
            this.pagination.currentPage = currentPage;
            this.pagination.perPage = perPage;

            const {
                data: { jobs, totalJobs }
            } = await this.fetchAllCompletedJobs(currentPage, perPage);

            this.assignJobcharges(jobs, totalJobs);

            this.$router.replace({ path: this.$route.path, query: { currentPage } });
            this.$_handleLoaderState(false);
        },
        removeOpenedRow() {
            if (this.selectedRow && this.expandedRow) {
                this.jobChargeList[this.jobChargeList.indexOf(this.selectedRow)].hasExpandedRow = false;
            }

            this.selectedJobs = [];
            this.shouldCloseDropdown = true;
            this.expandedRow = null;
        },
        handleRowClick(index) {
            const updatedIndex = index;

            // only one row can be expanded at a time
            if (this.selectedRow && this.expandedRow) {
                const selectedIndex = this.jobChargeList.indexOf(this.selectedRow);
                this.jobChargeList[this.jobChargeList.indexOf(this.selectedRow)].hasExpandedRow = false;

                this.selectedJobs.splice(this.selectedRow);

                if (selectedIndex === index) {
                    this.expandedRow = null;
                    return;
                }
            }

            this.jobChargeList[updatedIndex].hasExpandedRow = true;
            const row = this.jobChargeList[updatedIndex];

            const objCopy = Object.assign({}, row);
            objCopy.isExpanded = true;
            objCopy.hasExpandedRow = false;

            this.selectedRow = this.jobChargeList[updatedIndex];
            this.expandedRow = objCopy;

            this.selectedJobs.push(this.selectedRow);
        },
        async bulkRecalculate() {
            if (!this.isRecalculating) {
                await this.updateRates();
            }
        },
        async updateRates(item) {
            this.$messageBox
                .show({
                    class: 'sm-modal-container',
                    title: 'Recalculate',
                    body: item
                        ? `Are you sure you want to recalculate ${item.ref}?`
                        : `Are you sure you want to recalculated all job/s?`,
                    buttons: ['Confirm', 'Cancel']
                })
                .then(async (response) => {
                    if (response.toLowerCase() === 'confirm') {
                        this.isRecalculating = true;
                        this.showProgressBox = true;
                        this.totalToRecalculate = 0;
                        this.currentProcessed = 0;
                        this.isProgressComplete = false;

                        const stopIds = [];
                        const orderIds = [];
                        let createBatch = false;

                        if (item) {
                            if (item.stopId) {
                                if (item.rateGroupId) {
                                    stopIds.push(item.stopId);
                                    item.isRecalculating = true;
                                    item.hasExpandedRow = false;
                                }
                            }

                            if (item.orderId) {
                                if (item.rateGroupId) {
                                    orderIds.push(item.orderId);
                                    item.isRecalculating = true;
                                    item.hasExpandedRow = false;
                                }
                            }
                        } else {
                            this.selectedJobs.forEach((item) => {
                                if (item.stopId) {
                                    if (item.rateGroupId) {
                                        stopIds.push(item.stopId);
                                        item.isRecalculating = true;
                                        item.hasExpandedRow = false;
                                        createBatch = true;
                                    }
                                }

                                if (item.orderId) {
                                    if (item.rateGroupId) {
                                        orderIds.push(item.orderId);
                                        item.isRecalculating = true;
                                        item.hasExpandedRow = false;
                                        createBatch = true;
                                    }
                                }
                            });
                        }

                        this.removeOpenedRow();

                        this.isSelectAll = false;
                        const { teamId } = this.user;

                        const toUpdate = {
                            stopIds,
                            orderIds,
                            teamId
                        };

                        const payload = {
                            method: 'post',
                            data: toUpdate
                        };

                        if (stopIds.length <= 0 && orderIds.length <= 0) {
                            this.isRecalculating = false;
                            this.isProgressComplete = true;
                            return;
                        }

                        const api = createBatch ? `/api/rates/recalculate-rates-Batch` : `/api/rates/recalculate-rates`;
                        try {
                            await handleRequests(api, payload);
                        } catch (e) {
                            const message = `Cannot recalculate, please contact support if issue persists`;
                            showErrorMessage(this, message, e);
                        }
                    }

                    this.isRecalculating = false;
                });
        },
        async bulkApprove() {
            await this.approveJobs();
        },
        async approveJobs(item) {
            this.$messageBox
                .show({
                    class: 'sm-modal-container',
                    title: 'Approve',
                    body: item
                        ? `Are you sure you want to approve ${item.ref}?`
                        : `Are you sure you want to approve all job/s?`,
                    buttons: ['Confirm', 'Cancel']
                })
                .then(async (response) => {
                    if (response !== 'Confirm') {
                        return;
                    }

                    const approvalPayload = this.getApprovalPayload(item);
                    if (approvalPayload.stopIds.length <= 0 && approvalPayload.orderIds.length <= 0) {
                        return;
                    }

                    const payload = {
                        method: 'post',
                        data: approvalPayload
                    };

                    this.$_handleLoaderState(true, 'APPROVING...');

                    const api = `/api/process-charges/approve-jobs`;
                    try {
                        await handleRequests(api, payload);

                        this.$notifySuccess(item ? `Job ${item.ref} approved.` : 'Jobs approved.');
                        this.$_handleLoaderState(false);

                        this.selectedJobs = [];
                    } catch (e) {
                        const message = `Cannot approve, please contact support if issue persists`;
                        showErrorMessage(this, message, e);
                        this.$_handleLoaderState(false);
                    }

                    this.removeOpenedRow();
                    this.isSelectAll = false;
                });
        },
        async bulkUnapprove() {
            await this.unapproveJobs();
        },
        async unapproveJobs(item) {
            this.$messageBox
                .show({
                    class: 'sm-modal-container',
                    title: 'Unapprove',
                    body: item
                        ? `Are you sure you want to unapprove ${item.ref}?`
                        : `Are you sure you want to unapprove all job/s?`,
                    buttons: ['Confirm', 'Cancel']
                })
                .then(async (response) => {
                    if (response !== 'Confirm') {
                        return;
                    }

                    const approvalPayload = this.getApprovalPayload(item);
                    if (approvalPayload.stopIds.length <= 0 && approvalPayload.orderIds.length <= 0) {
                        return;
                    }

                    const payload = {
                        method: 'post',
                        data: approvalPayload
                    };

                    this.$_handleLoaderState(true, 'UNAPPROVING...');

                    const api = `/api/process-charges/unapprove-jobs`;
                    try {
                        await handleRequests(api, payload);

                        this.$notifySuccess(item ? `Job ${item.ref} unapproved.` : 'Jobs unapproved.');
                        this.$_handleLoaderState(false);

                        this.selectedJobs = [];
                    } catch (e) {
                        const message = `Cannot unapprove, please contact support if issue persists`;
                        showErrorMessage(this, message, e);
                        this.$_handleLoaderState(false);
                    }

                    this.removeOpenedRow();

                    this.isSelectAll = false;
                });
        },
        getApprovalPayload(item) {
            const stopIds = [];
            const orderIds = [];

            if (item) {
                item.hasExpandedRow = false;

                if (item.stopId) {
                    stopIds.push(item.stopId);
                }
                if (item.orderId) {
                    orderIds.push(item.orderId);
                }
            } else {
                this.selectedJobs.forEach((item) => {
                    item.hasExpandedRow = false;

                    if (item.stopId) {
                        stopIds.push(item.stopId);
                    }
                    if (item.orderId) {
                        orderIds.push(item.orderId);
                    }
                });
            }

            return {
                stopIds,
                orderIds
            };
        },
        async handleSelectTotalCostFilter(item) {
            this.selectedTotalCostDropdowns = item;
            const updateFilters = { ...this.filters, totalCostFilter: item.key };
            this.filters = Object.assign(this.filters, updateFilters);

            await this.handleFilterChanged(this.filters);
        },
        async handleSelectTotalChargeFilter(item) {
            this.selectedTotalChargeDropdowns = item;
            const updateFilters = { ...this.filters, totalChargeFilter: item.key };
            this.filters = Object.assign(this.filters, updateFilters);

            await this.handleFilterChanged(this.filters);
        },
        async handleFilterChanged(data) {
            this.filters = Object.assign(this.filters, data);
            this.loadJobs(1);
        },
        handleRecalculationCompleted(totalSuccess, failedRecords) {
            if (Number(failedRecords) <= 0) {
                this.isProgressComplete = true;
            } else {
                this.isProgressComplete = true;
                this.showProgressBox = false;

                this.$notify({
                    message: `Completed: ${totalSuccess} Failed  ${failedRecords}.<br /><br /> Partially recalculated, please try again. If error persists please contact support.`,
                    type: 'warning',
                    duration: 10000
                });
            }
        },
        handleOrderRecalculated(orderId, updateCosts, updateCharges) {
            const order = this.jobChargeList.find((s) => Number(orderId) === s.orderId);

            if (order) {
                this.fetchOrder(orderId, updateCosts, updateCharges);
            }

            this.currentProcessed = this.currentProcessed + 1;
        },
        handleTotalToBeRecalculated(total) {
            this.totalToRecalculate = Number(total);
        },
        handleCloseProgressBox() {
            this.showProgressBox = false;
        },
        async fetchOrder(orderId, updateCosts, updateCharges) {
            const endpoint = `/api/process-charges/order/${orderId}`;
            const response = await handleRequests(endpoint);
            const order = response.data;

            this.removeOpenedRow();

            // update the order's isRecalculating if it's still true
            for (let i = 0; i < this.jobChargeList.length; i++) {
                if (this.jobChargeList[i].orderId === orderId) {
                    this.jobChargeList.splice(i, 1, order);
                    break;
                }
            }
        },
        onOrderApprovalStatusUpdated(orderId, approved, unapproved) {
            const order = this.jobChargeList.find((s) => Number(orderId) === s.orderId);

            if (order) {
                order.isChargesApproved = approved;
            }
        },
        async updateServicePackage(item) {
            const data = this.setIsRecalculating(item, true);

            const payload = {
                method: 'patch',
                data
            };

            const endpoint = `/api/process-charges/update-service`;
            try {
                await handleRequests(endpoint, payload);
            } catch (e) {
                const message = `Cannot update service packages.`;
                showErrorMessage(this, message, e);
            }
        },
        handleScroll() {
            const element = document.querySelector('.sticky-selection');
            if (element) {
                const rect = element.getBoundingClientRect();
                this.isFixedSelector = rect.top <= 0;
            }
        },
        handleRecalculationFailed(error) {
            this.isProgressComplete = true;
            this.showProgressBox = false;

            this.$notify({
                message: `Recalculation failed. ${error}`,
                type: 'warning',
                duration: 10000
            });
        },
        toggleTableColumns() {
            const showClassNames = ['customer-name', 'service-name'];
            const hideClassNames = ['cost-container'];
            showClassNames.forEach((className) => {
                const elements = document.querySelectorAll(`.${className}`);
                elements.forEach((el) => {
                    el.style.display = 'inline-block';
                });
            });
            hideClassNames.forEach((className) => {
                const elements = document.querySelectorAll(`.${className}`);
                elements.forEach((el) => {
                    el.style.display = 'none';
                });
            });
        },
        setIsRecalculating(item, value) {
            const orderIds = [];

            item.isRecalculating = value;

            if (item.orderId) {
                orderIds.push(item.orderId);
            }

            const toUpdate = {
                rateGroupId: item.rateGroupId ?? null,
                orderIds
            };

            this.isRecalculating = true;
            this.showProgressBox = true;
            this.totalToRecalculate = 0;
            this.currentProcessed = 0;
            this.isProgressComplete = false;

            return toUpdate;
        },
        async handleOrderRatesUpdated(orderId, updateCosts, updateCharges) {
            const order = this.jobChargeList.find((s) => Number(orderId) === s.orderId);
            if (order) {
                await this.fetchOrder(orderId, updateCosts, updateCharges);
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.charges-editor-page {
    position: relative;
}

.custom-card {
    z-index: initial;
}

.body-list {
    position: relative;
}

.page-footer {
    padding: 0 15px;
}

.loading-position {
    margin: 20px auto;
}

.no-result-message {
    text-align: center;
    margin: 20px 0;
}

.sticky-selection {
    z-index: 1000;
    transition: top 0.1s ease;
}

.sticky-selection.fixed {
    position: sticky !important;
    top: 70px !important;
}

.bulk-section {
    position: absolute;
    top: -35px;
    width: 545px;
    margin-left: auto;
    margin-right: auto;
    left: 0;
    right: 0;
    text-align: center;
    background-color: #cfeefa;
    color: #2b93ff;
    font-weight: 400;
    padding: 5px 10px 5px 20px;

    > div {
        width: 60%;
        display: inline-block;
        vertical-align: middle;
        text-align: left;
    }

    > div:last-child {
        width: 40%;
        text-align: right;

        button {
            height: 30px;
            width: 30px;
            min-width: 30px;
        }
    }
}
.progress-container {
    min-width: 200px;
    max-width: 250px;
    background-color: #fff;
    position: fixed;
    right: 12px;
    bottom: 8px;
    z-index: 99;
    box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.14);
    padding: 0 10px 10px 10px;
    font-weight: 400;
    font-size: 13px;
    line-height: 18px;

    .progress-text-section {
        display: table;
        padding-top: 12px;

        > div {
            display: table-cell;
            vertical-align: middle;
        }

        > div:last-child {
            padding-left: 10px;
        }
    }

    ::v-deep .md-button {
        height: 20px;
        min-width: 20px;
        width: 20px;
        position: absolute;
        top: -12px;
        right: -12px;

        i {
            font-size: 15px !important;
        }
    }
}

.green-check {
    color: #4caf50 !important;
}

.btn-loader {
    border: 4px solid #eeeeee;
    border-radius: 50%;
    border-top: 4px solid #2b93ff;
    width: 24px;
    height: 24px;
    -webkit-animation: spin 1s linear infinite;
    animation: spin 1s linear infinite;
    top: 85%;
    left: 48%;
}

.route-loader {
    top: 20% !important;
    left: 0 !important;
}

.route-loader-text {
    left: 37px;
    position: absolute;
    top: 13px;
}


.hide-column {
    @media (max-width: 1200px) {
        display: none;
    }
}


@-webkit-keyframes spin {
    0% {
        -webkit-transform: rotate(0deg);
    }

    100% {
        -webkit-transform: rotate(360deg);
    }
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}
</style>
